import { z } from 'zod'
import {
  CRM_WORK_READ_CONTRACT_VERSION,
  CRM_WORK_READ_ERROR_CODES,
  crmTaskStatusSchema,
  crmTaskSummarySchema,
} from './crm-work-read'

/**
 * Transport request contracts keep the same field surface the CRM task handlers
 * accept today. Domain validation (required title, optional assignee, status enum)
 * stays in the route validators so existing 400 messages remain unchanged.
 */
export const crmTaskCreateRequestSchema = z.object({
  title: z.unknown().optional(),
  description: z.unknown().optional(),
  dueAt: z.unknown().optional(),
  due_at: z.unknown().optional(),
  assignedToUserId: z.unknown().optional(),
  assigned_to_user_id: z.unknown().optional(),
}).passthrough()

export const crmTaskPatchRequestSchema = z.object({
  status: z.unknown().optional(),
}).passthrough()

const crmTaskWriteSideEffectsSchema = z.object({
  db1Written: z.boolean(),
  auditWritten: z.boolean(),
  billingTouched: z.literal(false),
  eventEmitted: z.literal(false),
  uploadTouched: z.literal(false),
  llmTouched: z.literal(false),
  queuePublished: z.literal(false),
  db2Touched: z.literal(false),
  deployLiveTraffic: z.literal(false),
  destructiveCleanup: z.literal(false),
}).passthrough()

export const crmTaskWriteResponseSchema = z.object({
  contract_version: z.literal(CRM_WORK_READ_CONTRACT_VERSION),
  task: crmTaskSummarySchema,
  sideEffects: crmTaskWriteSideEffectsSchema,
}).passthrough()

export const crmTaskCreateResponseSchema = crmTaskWriteResponseSchema
export const crmTaskPatchResponseSchema = crmTaskWriteResponseSchema

export const CRM_TASK_WRITE_ERROR_CODES = CRM_WORK_READ_ERROR_CODES

export const crmTaskWriteErrorEnvelopeSchema = z.object({
  contract_version: z.string().min(1),
  error: z.object({
    code: z.enum(CRM_TASK_WRITE_ERROR_CODES),
    message: z.string().min(1),
    reference: z.string().min(1),
    details: z.record(z.unknown()).optional(),
    request_id: z.string().optional(),
  }).passthrough(),
}).passthrough()

export type CrmTaskCreateRequest = z.infer<typeof crmTaskCreateRequestSchema>
export type CrmTaskPatchRequest = z.infer<typeof crmTaskPatchRequestSchema>
export type CrmTaskWriteResponse = z.infer<typeof crmTaskWriteResponseSchema>
export type CrmTaskCreateResponse = z.infer<typeof crmTaskCreateResponseSchema>
export type CrmTaskPatchResponse = z.infer<typeof crmTaskPatchResponseSchema>
export type CrmTaskWriteErrorEnvelope = z.infer<typeof crmTaskWriteErrorEnvelopeSchema>
export type { CrmTaskStatus } from './crm-work-read'
export { crmTaskStatusSchema, crmTaskSummarySchema }
